// components/ProfileDialog.js

import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, Box, Typography,
  IconButton, Button, TextField, CircularProgress, Alert,
  Avatar, InputAdornment,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { loadDrawingList } from '../services/drawingStorage';

function formatDate(str) {
  if (!str) return '—';
  const d = new Date(str);
  if (isNaN(d)) return '—';
  return new Intl.DateTimeFormat('ru-RU', { day: '2-digit', month: 'long', year: 'numeric' }).format(d);
}

function formatDateShort(date) {
  if (!date) return '—';
  return new Intl.DateTimeFormat('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(date);
}

export default function ProfileDialog({ open, onClose }) {
  const { currentUser, userInitials, updateInitials, changePassword } = useAuth();

  const [userData, setUserData] = useState(null);
  const [drawingStats, setDrawingStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);

  // Редактирование инициалов
  const [editingInitials, setEditingInitials] = useState(false);
  const [initialsValue, setInitialsValue] = useState('');
  const [savingInitials, setSavingInitials] = useState(false);

  // Смена пароля
  const [changingPassword, setChangingPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPwd, setShowCurrentPwd] = useState(false);
  const [showNewPwd, setShowNewPwd] = useState(false);
  const [pwdLoading, setPwdLoading] = useState(false);
  const [pwdError, setPwdError] = useState('');
  const [pwdSuccess, setPwdSuccess] = useState(false);

  useEffect(() => {
    if (!open || !currentUser) return;
    setLoadingStats(true);

    const fetchData = async () => {
      try {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) setUserData(userDoc.data());

        const list = await loadDrawingList(currentUser.uid);
        setDrawingStats({
          count: list.length,
          lastSaved: list.length > 0 ? list[0].savedAt : null,
        });
      } catch (e) {
        console.error(e);
      } finally {
        setLoadingStats(false);
      }
    };
    fetchData();
  }, [open, currentUser]);

  const handleSaveInitials = async () => {
    setSavingInitials(true);
    try {
      await updateInitials(initialsValue.trim());
      setEditingInitials(false);
    } catch (e) {
      console.error(e);
    } finally {
      setSavingInitials(false);
    }
  };

  const handleChangePassword = async () => {
    setPwdError('');
    setPwdSuccess(false);
    if (newPassword !== confirmPassword) { setPwdError('Пароли не совпадают'); return; }
    if (newPassword.length < 6) { setPwdError('Пароль должен быть не менее 6 символов'); return; }
    setPwdLoading(true);
    try {
      await changePassword(currentPassword, newPassword);
      setPwdSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setChangingPassword(false);
    } catch (e) {
      if (e.code === 'auth/wrong-password' || e.code === 'auth/invalid-credential') {
        setPwdError('Неверный текущий пароль');
      } else {
        setPwdError('Ошибка: ' + e.message);
      }
    } finally {
      setPwdLoading(false);
    }
  };

  const emailInitial = currentUser?.email ? currentUser.email[0].toUpperCase() : '?';
  const displayInitials = userInitials || emailInitial;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '0.75rem',
          overflow: 'hidden',
          boxShadow: '0 1.5rem 4rem rgba(0,0,0,0.15)',
        }
      }}
    >
      {/* Шапка */}
      <DialogTitle sx={{ p: 0 }}>
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: 3, py: 2, background: '#fff', borderBottom: '0.0625rem solid #e0e0e0',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{
              width: '2.25rem', height: '2.25rem', borderRadius: '0.5rem',
              background: 'linear-gradient(135deg, #d32f2f, #e53935)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <PersonIcon sx={{ color: '#fff', fontSize: '1.25rem' }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#212121', lineHeight: 1.2 }}>Профиль</Typography>
              <Typography sx={{ fontSize: '0.7rem', color: '#9e9e9e', lineHeight: 1 }}>Настройки аккаунта</Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ color: '#9e9e9e', '&:hover': { color: '#212121', background: '#f5f5f5' } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, background: '#fafafa' }}>

        {/* Аватар и основная инфо */}
        <Box sx={{ px: 3, py: 2.5, background: '#fff', display: 'flex', alignItems: 'center', gap: 2, borderBottom: '0.0625rem solid #f0f0f0' }}>
          <Avatar sx={{
            width: '3.5rem', height: '3.5rem', fontSize: '1.4rem', fontWeight: 700,
            background: 'linear-gradient(135deg, #d32f2f, #e53935)',
            flexShrink: 0,
          }}>
            {displayInitials}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: '#212121', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {currentUser?.email}
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: '#9e9e9e', mt: 0.25 }}>
              {userData?.role === 'admin' ? 'Администратор' : userData?.role === 'engineer' ? 'Инженер' : 'Пользователь'}
            </Typography>
          </Box>
        </Box>

        {/* Инициалы */}
        <Box sx={{ px: 3, py: 2, background: '#fff', borderBottom: '0.0625rem solid #f0f0f0' }}>
          <Typography sx={{ fontSize: '0.72rem', color: '#9e9e9e', mb: 1, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Инициалы</Typography>
          {editingInitials ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                size="small"
                value={initialsValue}
                onChange={(e) => setInitialsValue(e.target.value)}
                placeholder="Например: ИИ"
                inputProps={{ maxLength: 3 }}
                autoFocus
                sx={{ flexGrow: 1, '& .MuiOutlinedInput-root': { borderRadius: '0.5rem', fontSize: '0.85rem' } }}
              />
              <IconButton size="small" onClick={handleSaveInitials} disabled={savingInitials} sx={{ color: '#43a047', '&:hover': { background: 'rgba(67,160,71,0.08)' } }}>
                {savingInitials ? <CircularProgress size={16} /> : <CheckIcon fontSize="small" />}
              </IconButton>
              <IconButton size="small" onClick={() => setEditingInitials(false)} sx={{ color: '#9e9e9e' }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography sx={{ fontSize: '0.85rem', color: userInitials ? '#212121' : '#bdbdbd' }}>
                {userInitials || 'Не указаны'}
              </Typography>
              <IconButton size="small" onClick={() => { setInitialsValue(userInitials || ''); setEditingInitials(true); }} sx={{ color: '#9e9e9e', '&:hover': { color: '#424242' } }}>
                <EditIcon sx={{ fontSize: '0.95rem' }} />
              </IconButton>
            </Box>
          )}
        </Box>

        {/* Статистика */}
        <Box sx={{ px: 3, py: 2, background: '#fff', borderBottom: '0.0625rem solid #f0f0f0' }}>
          <Typography sx={{ fontSize: '0.72rem', color: '#9e9e9e', mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Статистика</Typography>
          {loadingStats ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
              <CircularProgress size={20} sx={{ color: '#d32f2f' }} />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <InsertDriveFileOutlinedIcon sx={{ fontSize: '1rem', color: '#bdbdbd' }} />
                <Typography sx={{ fontSize: '0.82rem', color: '#616161' }}>
                  Сохранено чертежей: <Box component="span" sx={{ fontWeight: 600, color: '#212121' }}>{drawingStats?.count ?? '—'}</Box>
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <CalendarTodayIcon sx={{ fontSize: '1rem', color: '#bdbdbd' }} />
                <Typography sx={{ fontSize: '0.82rem', color: '#616161' }}>
                  Последнее сохранение: <Box component="span" sx={{ fontWeight: 600, color: '#212121' }}>{formatDateShort(drawingStats?.lastSaved)}</Box>
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <CalendarTodayIcon sx={{ fontSize: '1rem', color: '#bdbdbd' }} />
                <Typography sx={{ fontSize: '0.82rem', color: '#616161' }}>
                  Дата регистрации: <Box component="span" sx={{ fontWeight: 600, color: '#212121' }}>{formatDate(userData?.createdAt)}</Box>
                </Typography>
              </Box>
            </Box>
          )}
        </Box>

        {/* Смена пароля */}
        <Box sx={{ px: 3, py: 2, background: '#fff' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: changingPassword ? 1.5 : 0 }}>
            <Typography sx={{ fontSize: '0.72rem', color: '#9e9e9e', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Пароль</Typography>
            {!changingPassword && (
              <Button
                size="small"
                startIcon={<LockOutlinedIcon sx={{ fontSize: '0.9rem' }} />}
                onClick={() => { setChangingPassword(true); setPwdError(''); setPwdSuccess(false); }}
                sx={{ fontSize: '0.75rem', color: '#616161', '&:hover': { color: '#d32f2f' } }}
              >
                Сменить пароль
              </Button>
            )}
          </Box>

          {pwdSuccess && !changingPassword && (
            <Alert severity="success" sx={{ fontSize: '0.78rem', py: 0.5 }}>Пароль успешно изменён</Alert>
          )}

          {changingPassword && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              <TextField
                size="small" fullWidth label="Текущий пароль"
                type={showCurrentPwd ? 'text' : 'password'}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setShowCurrentPwd(p => !p)}>
                        {showCurrentPwd ? <VisibilityOffIcon sx={{ fontSize: '1rem' }} /> : <VisibilityIcon sx={{ fontSize: '1rem' }} />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '0.5rem', fontSize: '0.85rem' } }}
              />
              <TextField
                size="small" fullWidth label="Новый пароль"
                type={showNewPwd ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setShowNewPwd(p => !p)}>
                        {showNewPwd ? <VisibilityOffIcon sx={{ fontSize: '1rem' }} /> : <VisibilityIcon sx={{ fontSize: '1rem' }} />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '0.5rem', fontSize: '0.85rem' } }}
              />
              <TextField
                size="small" fullWidth label="Повторите новый пароль"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '0.5rem', fontSize: '0.85rem' } }}
              />
              {pwdError && <Alert severity="error" sx={{ fontSize: '0.78rem', py: 0.5 }}>{pwdError}</Alert>}
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Button size="small" onClick={() => { setChangingPassword(false); setPwdError(''); }} sx={{ color: '#9e9e9e' }}>
                  Отмена
                </Button>
                <Button
                  size="small" variant="contained" onClick={handleChangePassword}
                  disabled={pwdLoading || !currentPassword || !newPassword || !confirmPassword}
                  sx={{ background: '#d32f2f', '&:hover': { background: '#b71c1c' }, borderRadius: '0.4rem', fontSize: '0.78rem' }}
                >
                  {pwdLoading ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : 'Сохранить'}
                </Button>
              </Box>
            </Box>
          )}
        </Box>

      </DialogContent>

      <Box sx={{
        background: '#fff', borderTop: '0.0625rem solid #e0e0e0',
        px: 3, py: 1.25, display: 'flex', justifyContent: 'flex-end',
      }}>
        <Button size="small" onClick={onClose} variant="outlined"
          sx={{ fontSize: '0.75rem', borderColor: '#e0e0e0', color: '#616161', '&:hover': { borderColor: '#bdbdbd', background: '#f5f5f5' } }}
        >
          Закрыть
        </Button>
      </Box>
    </Dialog>
  );
}
